/* eslint-disable @typescript-eslint/member-ordering */

import { ChangeDetectionStrategy, Component, computed, effect, EffectRef, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CmsComponent, CmsSkeletonComponent } from '@libs/cms/components';
import { AppConfigModel, ContentConfig, PageModel } from '@libs/cms/models';
import { CategorySideMenuComponent } from '@libs/components/navigation';
import { SIDE_NAV_HIDDEN_PATHS } from '@libs/shared/common';
import { CapabilityService } from '@libs/shared/services';
import { NavItemModel } from '@libs/shared/models';
import { AppConfigService, PageMetadataService, PageViewService } from '@libs/shared/services';
import { of } from 'rxjs';
import { ContentAnchorScrollService } from '../../services';

@Component({
  selector: 'ui-content-page-route',
  templateUrl: './content-page-route.component.html',
  styleUrls: ['content-page-route.component.scss', 'content-page-route.component.lekkerland-2026.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CmsComponent,
    CmsSkeletonComponent,
    CategorySideMenuComponent,
  ],
  providers: [
    ContentAnchorScrollService,
  ],
})
export class ContentPageRouteComponent {
  private readonly pageViewService: PageViewService = inject(PageViewService);
  private readonly appConfigService: AppConfigService = inject(AppConfigService);
  private readonly capabilityService: CapabilityService = inject(CapabilityService);
  private readonly pageMetadataService: PageMetadataService = inject(PageMetadataService);
  private readonly contentAnchorScrollService: ContentAnchorScrollService = inject(ContentAnchorScrollService);

  private readonly isConfigMode: Signal<boolean> = this.capabilityService.isAvailable('platform.cmsAppConfigMode');

  public config: WritableSignal<ContentConfig | undefined> = signal(undefined);
  public showLoader: WritableSignal<boolean> = signal(false);
  public hasContent: WritableSignal<boolean | undefined> = signal(undefined);

  public tree: Signal<NavItemModel | undefined> = toSignal(this.pageViewService.getTree());
  public current: Signal<NavItemModel | undefined> = toSignal(this.pageViewService.getCurrentNode());
  public breadcrumbs: Signal<NavItemModel[] | undefined> = toSignal(this.pageViewService.getBreadcrumbNodes());
  public hideNavigation: Signal<boolean | undefined> = toSignal(this.pageViewService.getHideNavigation());

  public readonly selectedItemId: Signal<string | undefined> = computed(() => {
    const current: NavItemModel | undefined = this.current();

    return current?.id?.toString();
  });

  public readonly isSideNavHidden: Signal<boolean> = computed(() => {
    const url: string = this.current()?.url ?? '';

    return SIDE_NAV_HIDDEN_PATHS.some((path: string) => url.includes(path));
  });

  public readonly navItems: Signal<readonly NavItemModel[]> = computed(() => {
    if (this.hideNavigation() || this.isSideNavHidden()) {
      return [];
    }

    const node: NavItemModel | undefined = this.breadcrumbs()?.[1];

    return node?.children?.length ? [node] : [];
  });

  private readonly contentConfig: Signal<ContentConfig | undefined> = toSignal(this.pageViewService.getContentConfig());
  private readonly appConfig: Signal<AppConfigModel | undefined> = toSignal(this.isConfigMode() ? this.appConfigService.getConfig() : of(undefined));

  private readonly contentConfigEffect: EffectRef = effect((): void => {
    const config: ContentConfig | undefined = this.contentConfig();

    if (!config) {
      return;
    }

    this.showLoader.set(true);
    this.hasContent.set(false);
    this.config.set(config);
  });

  public onLoadResult(hasContent: boolean): void {
    this.showLoader.set(false);
    this.hasContent.set(hasContent);

    if (hasContent) {
      this.contentAnchorScrollService.scheduleScrollToCurrentAnchor();
    }
  }

  public onPageLoaded(page: PageModel): void {
    // The current path can change before the new config reaches <ui-cms>.
    if (this.config() !== this.contentConfig()) {
      return;
    }

    this.pageViewService.setAvailableLanguages(page.availableLanguages);

    if (this.isConfigMode()) {
      this.pageMetadataService.update(page, this.appConfig()?.company.title);
    }
  }
}
